import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import {
  CheckCircle2,
  Clock3,
  Calendar,
  Download,
  Loader2,
  MailOpen,
  Ticket as TicketIcon,
  XCircle,
} from "lucide-react";
import { API_BASE } from "@/lib/supabase";
import { formatPrice, formatEventDateTime } from "@/components/site/EventCard";

const POLL_MS = 3000;
const MAX_POLL_ATTEMPTS = 20;

function StatusPill({ status, paid, payStatus }) {
  // "Betaling op locatie" (see add_ticket_type_pay_on_location.sql) -- paid/status
  // are the same as any other confirmed order (the ticket is usable immediately),
  // but no money has actually gone through Pay.nl, so "Betaald" would be a false claim.
  if (payStatus === "PAY_ON_LOCATION") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-svj-cyan/10 border border-svj-cyan/40 text-svj-cyan px-3 py-1 text-xs font-bold uppercase tracking-widest">
        <CheckCircle2 className="h-3.5 w-3.5" /> Aangemeld — betalen ter plekke
      </span>
    );
  }
  if (paid || status === "paid") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/40 text-emerald-400 px-3 py-1 text-xs font-bold uppercase tracking-widest">
        <CheckCircle2 className="h-3.5 w-3.5" /> Betaald
      </span>
    );
  }
  if (status === "payment_pending" || status === "created") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-svj-orange/10 border border-svj-orange/40 text-svj-orange px-3 py-1 text-xs font-bold uppercase tracking-widest">
        <Clock3 className="h-3.5 w-3.5" /> Wacht op betaling
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-red-500/10 border border-red-500/40 text-red-400 px-3 py-1 text-xs font-bold uppercase tracking-widest">
      <XCircle className="h-3.5 w-3.5" /> {status || "onbekend"}
    </span>
  );
}

export default function Confirmation() {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const fetchOrder = async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/orders/${orderId}`);
      setOrder(data);
      return data;
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      // Dev-only helper: if we came in via checkout with ?simulate=1, mark order as paid
      // (only usable when Pay.nl is NOT configured; production requires the cron secret).
      if (params.get("simulate") === "1") {
        try {
          await axios.post(`${API_BASE}/orders/${orderId}/simulate-pay`);
        } catch (e) {
          /* silently ignore — Pay.nl webhook / polling will finalise the order */
        }
      }
      await fetchOrder();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  // Poll if not yet paid (Pay.nl webhook fallback)
  useEffect(() => {
    if (!order) return;
    if (order.paid || order.status === "paid" || order.status === "failed" || order.status === "cancelled") return;
    if (attempts >= MAX_POLL_ATTEMPTS) return;
    const t = setTimeout(async () => {
      setAttempts((a) => a + 1);
      // Ask backend to poll Pay.nl (webhook fallback) then re-read order
      try {
        await axios.post(`${API_BASE}/orders/${orderId}/refresh`);
      } catch {
        /* non-fatal */
      }
      await fetchOrder();
    }, POLL_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, attempts]);

  const downloadPdf = () => {
    // Trigger backend PDF (returned as binary) — endpoint returns URL or a redirect.
    window.open(`${API_BASE}/orders/${orderId}/pdf`, "_blank");
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center text-slate-400">
        <Loader2 className="h-6 w-6 mx-auto animate-spin text-svj-cyan" />
      </div>
    );
  }
  if (error || !order) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <div className="text-white font-semibold" data-testid="order-error">
          {error || "Bestelling niet gevonden"}
        </div>
        <Link to="/events" className="mt-6 inline-flex text-svj-cyan hover:text-white font-semibold">
          Terug naar events
        </Link>
      </div>
    );
  }

  const isPaid = order.paid || order.status === "paid";
  const isPayOnLocation = order.pay_status === "PAY_ON_LOCATION";

  return (
    <div data-testid="confirmation-page" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
      <div className="bg-svj-card border border-svj-border rounded-2xl p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-svj-cyan font-semibold">
              Bestelling
            </div>
            <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
              {order.event_title}
            </h1>
            {order.event_starts_at && (
              <div
                className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-svj-cyan"
                data-testid="order-event-datetime"
              >
                <span className="inline-flex items-center gap-1.5">
                  <Calendar className="h-4 w-4" />
                  {formatEventDateTime(order.event_starts_at)}
                </span>
                {order.event_ends_at && (
                  <span className="inline-flex items-center gap-1.5">
                    <Clock3 className="h-4 w-4" />
                    Tot {formatEventDateTime(order.event_ends_at)}
                  </span>
                )}
              </div>
            )}
            <div className="mt-2 text-xs text-slate-400" data-testid="order-id">
              Ordernummer: {order.id}
            </div>
          </div>
          <StatusPill status={order.status} paid={order.paid} payStatus={order.pay_status} />
        </div>

        {!isPaid && (
          <div
            className="mt-6 rounded-xl border border-svj-orange/40 bg-svj-orange/10 p-4 text-svj-orange text-sm flex items-start gap-2"
            data-testid="order-pending-hint"
          >
            <Clock3 className="h-4 w-4 mt-0.5" />
            <div>
              We wachten op de betaalbevestiging. Dit venster ververst automatisch. Sluit deze pagina niet — je tickets verschijnen zodra de betaling binnen is.
            </div>
          </div>
        )}

        {isPaid && isPayOnLocation && (
          <div
            className="mt-6 rounded-xl border border-svj-cyan/40 bg-svj-cyan/10 p-4 text-svj-cyan text-sm flex items-start gap-2"
            data-testid="order-paid-hint"
          >
            <MailOpen className="h-4 w-4 mt-0.5" />
            <div>
              Je aanmelding is bevestigd! Je betaalt het bedrag ter plekke bij het evenement. Je tickets staan
              hieronder en zijn ook per e-mail verzonden naar{" "}
              <span className="font-semibold">{order.customer_email}</span>.
            </div>
          </div>
        )}

        {isPaid && !isPayOnLocation && (
          <div
            className="mt-6 rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-4 text-emerald-300 text-sm flex items-start gap-2"
            data-testid="order-paid-hint"
          >
            <MailOpen className="h-4 w-4 mt-0.5" />
            <div>
              Betaling gelukt! Je tickets staan hieronder en zijn ook per e-mail verzonden naar{" "}
              <span className="font-semibold">{order.customer_email}</span>.
            </div>
          </div>
        )}

        <div className="mt-8">
          <div className="text-sm font-semibold text-white uppercase tracking-wider mb-3">
            Bestelde tickets
          </div>
          <div className="divide-y divide-svj-border">
            {(order.items || []).map((i) => (
              <div key={i.id} className="py-3 flex justify-between text-sm">
                <span className="text-slate-300">
                  {i.qty}× <span className="text-white font-medium">{i.ticket_name}</span>
                </span>
                <span className="text-white font-semibold">
                  {i.subtotal_cents === 0 ? "Gratis" : formatPrice(i.subtotal_cents)}
                </span>
              </div>
            ))}
          </div>
          <div className="mt-4 border-t border-svj-border pt-4 grid grid-cols-2 gap-2 text-sm">
            <span className="text-slate-400">Subtotaal (excl. btw)</span>
            <span className="text-right text-white">{formatPrice(order.subtotal_cents)}</span>
            <span className="text-slate-400">Btw</span>
            <span className="text-right text-white">{formatPrice(order.vat_cents)}</span>
            <span className="text-white font-semibold">Totaal</span>
            <span className="text-right text-2xl font-extrabold text-svj-cyan" data-testid="order-total">
              {order.total_cents === 0 ? "Gratis" : formatPrice(order.total_cents)}
            </span>
          </div>
        </div>

        {isPaid && (order.tickets?.length ?? 0) > 0 && (
          <div className="mt-10">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <TicketIcon className="h-4 w-4 text-svj-cyan" />
                <div className="text-sm font-semibold text-white uppercase tracking-wider">
                  Jouw tickets ({order.tickets.length})
                </div>
              </div>
              <button
                type="button"
                onClick={downloadPdf}
                data-testid="order-download-pdf"
                className="inline-flex items-center gap-2 rounded-full bg-transparent border-2 border-svj-cyan text-svj-cyan font-bold py-2 px-4 text-sm hover:bg-svj-cyan/10 transition-colors"
              >
                <Download className="h-4 w-4" /> PDF downloaden
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {order.tickets.map((t) => (
                <div
                  key={t.id}
                  data-testid={`ticket-item-${t.id}`}
                  className="relative flex bg-svj-bg border border-svj-border rounded-2xl overflow-hidden"
                >
                  <div className="flex-1 p-5 border-r-2 border-dashed border-svj-border relative svj-perf">
                    <div className="text-xs uppercase tracking-widest text-svj-cyan font-semibold">
                      {t.ticket_name}
                    </div>
                    <div className="mt-1 text-white font-bold text-lg leading-tight">
                      {order.event_title}
                    </div>
                    {t.holder_name && (
                      <div className="mt-1 text-xs text-slate-400">Op naam van {t.holder_name}</div>
                    )}
                    {(t.registered_days || []).length > 0 && (
                      <div className="mt-1 text-xs text-svj-cyan" data-testid={`ticket-days-${t.id}`}>
                        Aangemeld voor:{" "}
                        {t.registered_days
                          .map((d) => d.label || `Dag ${d.day_number}`)
                          .join(", ")}
                      </div>
                    )}
                    <div className="mt-3 text-[10px] uppercase tracking-widest text-slate-500">
                      Ticketcode
                    </div>
                    <div className="text-xs font-mono text-slate-200 break-all">{t.code}</div>
                  </div>
                  <div className="w-32 sm:w-40 p-4 flex items-center justify-center bg-white">
                    <QRCodeSVG value={t.code} size={112} level="M" includeMargin={false} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="mt-8 text-center">
        <Link to="/events" className="text-sm text-slate-400 hover:text-white">
          Terug naar alle events
        </Link>
      </div>
    </div>
  );
}
